import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { POPUP_DISPLAY_SCREEN_OPTION } from "helpers/constans";
import UploadFileBK from "components/Common/UploadFileBK";
import "react-phone-number-input/style.css";
import DatePicker from "react-datepicker";
import IconJobList from "../../assets/images/icons/ic_ms_shop.svg";
import IconManager from "../../assets/images/icons/ic_ms_edit.svg";
import IconTalent from "../../assets/images/icons/ic_ms_search.svg";
import IconCommunity from "../../assets/images/icons/ic_community.svg";
import IconChat from "../../assets/images/icons/ic_chat.svg";
import { getPopupDetail, postPopup, putPopup } from "store/popup/thunk";
import { formatDateStringToKorea, formatISOString } from "helpers/format";

export interface Props {
  id?: string;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  setLinkImage?: any;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const PopupManagerForm = ({
  id = "",
  triggerRefresh,
  isShowDetail = false,
  setLinkImage,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const data = {
        imageUrl: values?.imageUrl?.length > 0 ? values?.imageUrl?.[0]?.url : "",
        startDate: values?.startDate ? formatISOString(values?.startDate) : "",
        endDate: values?.endDate ? formatISOString(values?.endDate) : "",
        show_sections: values?.show_sections,
        isActive: values?.isActive,
        ...(values?.url ? { url: values?.url } : {}),
      };

      const response: any = id
        ? await putPopup(id, data)
        : await postPopup(data);

      if (response?.data || response?.code == 200) {
        setIsLoading((_prev) => false);
        toast(
          `${
            id
              ? t("The popup has been updated.")
              : t("The popup has been created.")
          }`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    imageUrl: Yup.array()
      .of(
        Yup.object({
          url: Yup.string().nullable(),
        }).required(`${t("This field is required")}`),
      ).required(`${t("This field is required")}`),
    url: Yup.string().matches(
      /^https:\/\/.+/,
      t("URL must start with https://")
    ),
    startDate: Yup.string().required(`${t("This field is required")}`),
    endDate: Yup.string().required(`${t("This field is required")}`),
    show_sections: Yup.array()
      .min(1, `${t("This field is required")}`)
      .required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      imageUrl: "",
      url: "",
      startDate: null,
      endDate: null,
      isActive: true,
      show_sections: [] as string[],
      limit_date: "7d"
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    console.log(valueDefault)
    formik.setValues({
      ...valueDefault,
      imageUrl: valueDefault?.imageUrl ? [{url: valueDefault?.imageUrl}] : [],
      show_sections: valueDefault?.show_sections ?? [],
      startDate: valueDefault?.startDate ? new Date(formatDateStringToKorea(valueDefault?.startDate)) : null,
      endDate: valueDefault?.endDate ? new Date(formatDateStringToKorea(valueDefault?.endDate)) : null,
    });
  };

  // Helper for "전체 (ALL)" select behavior
  const allDisplayValues = POPUP_DISPLAY_SCREEN_OPTION.map((o: any) => o.value);
  const isAllSelected = (): boolean =>
    Array.isArray(formik.values.show_sections) &&
    allDisplayValues.every((v) => formik.values.show_sections.includes(v));

  const toggleAll = () => {
    if (isAllSelected()) {
      formik.setFieldValue("show_sections", []);
    } else {
      formik.setFieldValue("show_sections", [...allDisplayValues]);
    }
  };

  const toggleItem = (value: string) => {
    const current = Array.isArray(formik.values.show_sections)
      ? [...formik.values.show_sections]
      : [];
    const idx = current.indexOf(value);
    if (idx > -1) {
      current.splice(idx, 1);
    } else {
      current.push(value);
    }
    formik.setFieldValue("show_sections", current);
  };

  // helper to build icon URL from public assets folder
  const getIconUrl = (value: string) => {
    switch (value) {
      case "home":
        return IconJobList;
      case "job_posting":
      case "job-post":
        return IconManager;
      case "find-talent":
        return IconTalent;
      case "community":
        return IconCommunity;
      case "chat":
        return IconChat;
      default:
        break;
    }
  };

  const handleCallPopupDetail = async (idItem: string) => {
    if (!idItem) return;

    try {
      setIsLoadingDetail((_prev) => true);
      const resDetail: any = await getPopupDetail(idItem);

      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  useEffect(() => {
    handleCallPopupDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create Popup")} - ${t("Popup")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="l">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Popup") : t("Create Popup")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 5) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              <form onSubmit={formik.handleSubmit}>
                <Row
                  className="modal-custom-scroll"
                  style={{ overflowY: "auto", maxHeight: "68vh" }}
                >
                  <Col xxl={12}>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("Popup Image")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <UploadFileBK
                        formik={formik}
                        fieldValue="imageUrl"
                        isMultiple={false}
                        isUploadMore={
                          !formik?.values?.imageUrl ||
                          !(formik?.values?.imageUrl as any)?.length
                        }
                        setLinkImage={setLinkImage}
                      />
                      {formik.touched.imageUrl && formik.errors.imageUrl ? (
                        <div className="text-danger mt-2">
                          {formik.errors.imageUrl}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="url" className="form-label">
                        {t("URL (click to go)")}{" "}
                      </Label>
                      <Input
                        id="url"
                        name="url"
                        type="text"
                        className="form-control"
                        placeholder={t("https://... 또는 onnyjob://...")}
                        value={formik?.values?.url}
                        onChange={(event: any) =>
                          formik.setFieldValue("url", event?.target?.value)
                        }
                      />
                      <small className="text-secondary">
                        http/https 또는 onnyjob:// 모두 허용
                      </small>
                      {formik.touched.url && formik.errors.url ? (
                        <div className="text-danger mt-2">
                          {formik.errors.url}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <Label htmlFor="url" className="form-label">
                      {t("Display Period")}{" "}
                    </Label>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="startDate" className="form-label">
                        {t("Popup Start Date")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <div className="w-100">
                        <DatePicker
                          name={`startDate`}
                          className="form-control w-100"
                          selected={formik?.values?.startDate}
                          placeholderText={t("Select")}
                          onChange={(date: any) => {
                            formik.setFieldValue(`startDate`, date);
                          }}
                          dateFormat="yyyy-MM-dd"
                          isClearable={true}
                        />
                        {formik.touched.startDate && formik.errors.startDate ? (
                          <div className="text-danger mt-2">
                            {formik.errors.startDate}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="endDate" className="form-label">
                        {t("Popup End Date")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <div className="w-100">
                        <DatePicker
                          name={`endDate`}
                          className="form-control w-100"
                          selected={formik?.values?.endDate}
                          placeholderText={t("Select")}
                          onChange={(date: any) => {
                            formik.setFieldValue(`endDate`, date);
                          }}
                          dateFormat="yyyy-MM-dd"
                          isClearable={true}
                        />
                        {formik.touched.endDate && formik.errors.endDate ? (
                          <div className="text-danger mt-2">
                            {formik.errors.endDate}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="postal_code" className="form-label">
                        {t("Select Display Screen")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <div className="">
                        {[
                          { value: "all", label: t("Select All") },
                          ...POPUP_DISPLAY_SCREEN_OPTION,
                        ].map((item: any) => (
                          <div
                            key={item.value}
                            className="d-flex gap-2 align-items-center mb-2 form-check"
                          >
                            <Input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id={`benifit-${item.value}`}
                              name={`benifit-${item.value}`}
                              checked={
                                item.value === "all"
                                  ? isAllSelected()
                                  : Array.isArray(
                                      formik.values.show_sections
                                    ) &&
                                    formik.values.show_sections.includes(
                                      item.value
                                    )
                              }
                              onChange={() =>
                                item.value === "all"
                                  ? toggleAll()
                                  : toggleItem(item.value)
                              }
                            />

                            <Label
                              className="mb-0 d-flex align-items-center gap-1"
                              htmlFor={`benifit-${item.value}`}
                            >
                              {item.value !== "all" && (
                                <img
                                  src={getIconUrl(item.value)}
                                  alt=""
                                  style={{
                                    width: 20,
                                    height: 20,
                                    objectFit: "contain",
                                  }}
                                  onError={(e: any) => {
                                    const el =
                                      e?.target as HTMLImageElement | null;
                                    if (el) el.style.display = "none";
                                  }}
                                />
                              )}

                              {t(item.label)}
                            </Label>
                          </div>
                        ))}
                        {formik.touched.show_sections && formik.errors.show_sections ? (
                          <div className="text-danger mt-2">
                            {formik.errors.show_sections}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <div>{t("Whether exposed or not")}</div>
                          <small className="text-secondary">
                            {t(
                              "When activated, other pop-ups are automatically disabled."
                            )}
                          </small>
                        </div>

                        <div
                          className="form-check form-switch form-switch-sm text-center"
                          dir="ltr"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="isActive"
                            checked={
                              formik?.values?.isActive === true ? true : false
                            }
                            onChange={(event) => {
                              formik.setFieldValue(
                                "isActive",
                                event?.target?.checked
                              );
                            }}
                          />
                        </div>
                      </div>

                      {formik?.touched?.isActive && formik?.errors?.isActive ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.isActive}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Button Update Popup")
                          : t("Button Create Popup")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default PopupManagerForm;
